import { useEffect, useState } from "react";
import moment from "moment";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import ClearIcon from "@mui/icons-material/Clear";
import { useDispatch } from "react-redux";
import {
  getSupplierActivity,
  getSupplierProductViews,
} from "app/redux/slice/supplierSlice";

const fmt = (date) => (date ? moment(date).format("DD-MM-YYYY hh:mm A") : "—");

const Stat = ({ label, value, hint }) => (
  <Grid item xs={6} sm={3}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h4" mb={0}>
      {value}
    </Typography>
    {hint && (
      <Typography variant="caption" color="text.secondary">
        {hint}
      </Typography>
    )}
  </Grid>
);

// Read-only view of what a vendor has actually been doing in the App.
//
// The cart shown here is the live app cart, not a snapshot - it is read from
// the same collection the vendor's own cart screen uses.
const VendorActivity = ({ vendor, onClose }) => {
  const dispatch = useDispatch();
  const [tab, setTab] = useState(0);
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);

  const [views, setViews] = useState({ list: [], total: 0 });
  const [viewsLoading, setViewsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!vendor?._id) return;
    setLoading(true);
    dispatch(getSupplierActivity(vendor._id))
      .unwrap()
      .then((res) => {
        setActivity(res?.data?.data ?? null);
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendor?._id]);

  const loadViews = (nextPage = page, term = search) => {
    if (!vendor?._id) return;
    setViewsLoading(true);
    dispatch(
      getSupplierProductViews({
        id: vendor._id,
        page: nextPage + 1,
        perPage,
        ...(term.trim() && { search: term.trim() }),
      }),
    )
      .unwrap()
      .then((res) => {
        setViews({
          list: res?.data?.data?.viewList ?? [],
          total: res?.data?.data?.total ?? 0,
        });
        setViewsLoading(false);
      });
  };

  useEffect(() => {
    if (tab === 1) loadViews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, page, perPage]);

  if (loading) {
    return (
      <Stack alignItems="center" sx={{ py: 5 }}>
        <CircularProgress />
      </Stack>
    );
  }

  if (!activity) return <Typography>Could not load this vendor.</Typography>;

  const { login, views: viewStats, cart, orderCount } = activity;

  return (
    <>
      {/* The headline answer: did this vendor ever get into the app at all? */}
      {login.hasLoggedIn ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          Logged in {login.loginCount} time(s). Last seen {fmt(login.lastLoginAt)}.
        </Alert>
      ) : (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This vendor has never logged in to the app.
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 1 }}>
        <Stat label="Logins" value={login.loginCount || 0} />
        <Stat
          label="Products Viewed"
          value={viewStats.productsViewed}
          hint={`${viewStats.totalViews} total views`}
        />
        <Stat label="In Cart" value={cart.lineCount} hint={`${cart.totalQuantity} qty`} />
        <Stat label="Orders" value={orderCount} />
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Login" />
        <Tab label={`Viewed Products (${viewStats.productsViewed})`} />
        <Tab label={`Cart (${cart.lineCount})`} />
      </Tabs>

      {tab === 0 && (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              First login
            </Typography>
            <Typography>{fmt(login.firstLoginAt)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Last login
            </Typography>
            <Typography>{fmt(login.lastLoginAt)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Total logins
            </Typography>
            <Typography>{login.loginCount || 0}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Devices registered for push
            </Typography>
            <Typography>{login.deviceCount}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              Vendor added on
            </Typography>
            <Typography>
              {fmt(activity.supplier.createdAt)} ({activity.supplier.createdFrom})
            </Typography>
          </Grid>
        </Grid>
      )}

      {tab === 1 && (
        <>
          <TextField
            fullWidth
            size="small"
            label="Search product"
            value={search}
            sx={{ mb: 2 }}
            onChange={(e) => setSearch(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                setPage(0);
                loadViews(0, search);
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment
                  position="end"
                  sx={{ display: search === "" ? "none" : "flex", cursor: "pointer" }}
                  onClick={() => {
                    setSearch("");
                    setPage(0);
                    loadViews(0, "");
                  }}
                >
                  <ClearIcon />
                </InputAdornment>
              ),
            }}
          />
          <TableContainer sx={{ maxHeight: 420 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell align="center">Times Viewed</TableCell>
                  <TableCell>First Viewed</TableCell>
                  <TableCell>Last Viewed</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {viewsLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <CircularProgress size={22} />
                    </TableCell>
                  </TableRow>
                ) : views.list.length ? (
                  views.list.map((row) => (
                    <TableRow key={row._id}>
                      <TableCell>{row.productName}</TableCell>
                      <TableCell>{row.sku || "—"}</TableCell>
                      <TableCell align="center">
                        <Chip size="small" label={row.viewCount} color="info" />
                      </TableCell>
                      <TableCell>{fmt(row.firstViewedAt)}</TableCell>
                      <TableCell>{fmt(row.lastViewedAt)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      This vendor has not opened any product yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={views.total}
            page={page}
            rowsPerPage={perPage}
            rowsPerPageOptions={[10, 25, 50]}
            onPageChange={(e, p) => setPage(p)}
            onRowsPerPageChange={(e) => {
              setPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </>
      )}

      {tab === 2 && (
        <TableContainer sx={{ maxHeight: 460 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell>Variant</TableCell>
                <TableCell>Colour</TableCell>
                <TableCell align="center">Qty</TableCell>
                <TableCell align="center">Approx KG</TableCell>
                <TableCell>Added</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cart.lines.length ? (
                cart.lines.map((line) => (
                  <TableRow key={line._id}>
                    <TableCell>{line.productName}</TableCell>
                    <TableCell>{line.sku}</TableCell>
                    <TableCell>{line.color || "—"}</TableCell>
                    <TableCell align="center">{line.quantity}</TableCell>
                    <TableCell align="center">
                      {line.approxWeightKg ? line.approxWeightKg : "—"}
                    </TableCell>
                    <TableCell>{fmt(line.addedAt)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    The cart is empty.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Box sx={{ mt: 2 }}>
        <Stack direction="row" justifyContent="flex-end">
          <LoadingButton variant="outlined" color="secondary" onClick={onClose}>
            Close
          </LoadingButton>
        </Stack>
      </Box>
    </>
  );
};

export default VendorActivity;
